<script lang="ts" setup>
import type { DialogContentEmits, DialogContentProps } from "reka-ui"
import type { HTMLAttributes } from "vue"
import { computed, inject, nextTick, onBeforeUnmount, ref, watch } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { useForwardPropsEmits } from "reka-ui"
import { DrawerContent, DrawerPortal } from "vaul-vue"
import { cn } from "@/lib/utils"
import DrawerOverlay from "./DrawerOverlay.vue"
import { DRAWER_STACK_KEY, drawerStackSize, reportSheetHeight, stackSheetHeight } from "~/composables/useDrawerStack"

defineOptions({
  inheritAttrs: false,
})

// `overlayClass` exists so a drawer that has to sit above another drawer can raise *both* layers:
// content and overlay each carry their own `z-50`, and lifting only the content leaves the dim
// backdrop stranded below whatever it's supposed to be covering. See UserProfileDialog.vue.
const props = defineProps<DialogContentProps & {
  class?: HTMLAttributes["class"]
  overlayClass?: HTMLAttributes["class"]
}>()
const emits = defineEmits<DialogContentEmits>()

// Neither is a vaul/reka prop — keep them off the forwarded set so they don't land on the DOM node.
const forwarded = useForwardPropsEmits(reactiveOmit(props, "class", "overlayClass"), emits)

// iOS-style card stack (see useDrawerStack.ts): while other Drawers are open on top of this one it
// recedes — a little narrower and nudged up, so its top edge stays visible above the card in front —
// one step per Drawer above it. Same geometry and easing vaul uses for its own nested Drawers
// (16px narrower, 16px up, 0.5s cubic-bezier(.32,.72,0,1)).
// Done with the standalone `scale`/`translate` properties rather than `transform`: vaul writes
// inline `transform` on this element itself (open/close animation, drag), and the individual
// properties compose with it instead of fighting over it.
const stack = inject(DRAWER_STACK_KEY, null)
const stackDepth = computed(() => stack?.depth.value ?? 0)
const isDrawerOpen = computed(() => stack?.isOpen.value ?? true)
const STACK_STEP_PX = 16
const STACK_EASING = "cubic-bezier(0.32, 0.72, 0, 1)"

// Only bottom sheets line up to a shared height — side Drawers (the full-height chat) are sized by
// their insets. The sheet element is reached through the handle bar (its first child) rather than a
// component ref on vaul's DrawerContent, whose `$el` isn't the sheet itself (it renders through a
// Presence), and it's watched rather than read in `onMounted` because that content renders after
// this wrapper mounts.
const handleRef = ref<HTMLElement | null>(null)
const sheetEl = () => handleRef.value?.parentElement ?? null
const direction = ref<string | undefined>()
let heightAnimation: Animation | null = null

// The sheet's natural height: with any shared height lifted for the duration of the read (same
// frame, never painted), so it's what the content itself needs — still capped by the sheet's own
// `max-h-[80vh]`. `offsetHeight` ignores the recede's `scale`. Deferred while a height animation is
// running, since that would be read back instead.
function measureNaturalHeight() {
  const el = sheetEl()
  if (!el || direction.value !== "bottom" || !stack) return
  if (heightAnimation?.playState === "running") {
    void heightAnimation.finished.then(measureNaturalHeight, () => {})
    return
  }
  const { height, maxHeight } = el.style
  el.style.height = ""
  el.style.maxHeight = ""
  // Scroll areas inside the sheet (`ScrollArea` with `flex-1 min-h-0`, the usual sheet body) shrink
  // to whatever room the sheet leaves them instead of growing it, so the sheet's own height alone
  // under-reports what its content needs — the cache section read 216px with a whole donut chart and
  // list inside. Add back what each of them is holding out of view.
  // It's the ScrollArea root (`overflow-hidden`, squeezed by the flex layout) that clips, while its
  // viewport grows to the content — so compare the content's full height to the root's box.
  // The largest shortfall, not the sum: sheets with slide navigation (MobileProfileDrawer.vue) put
  // their screens side by side in one strip, each with its own scroll area, and it's the tallest
  // screen that decides the strip's height — summing them would stack heights that sit next to
  // each other.
  let hidden = 0
  for (const viewport of el.querySelectorAll<HTMLElement>('[data-slot="scroll-area-viewport"]')) {
    const root = viewport.parentElement
    if (!root) continue
    hidden = Math.max(hidden, viewport.scrollHeight - root.clientHeight)
  }
  const natural = el.offsetHeight + hidden
  el.style.height = height
  el.style.maxHeight = maxHeight
  ownNaturalHeightPx.value = natural
  reportSheetHeight(stack.id, natural)
}

// Content that arrives after the sheet opens (e.g. the cache section's stats replacing its spinner)
// changes what it needs, so the natural height is re-read whenever the sheet's DOM changes — at most
// once per frame.
let contentObserver: MutationObserver | null = null
let measureFrame: number | null = null
function scheduleMeasure() {
  if (measureFrame !== null) return
  measureFrame = requestAnimationFrame(() => {
    measureFrame = null
    measureNaturalHeight()
  })
}

// Swipe-down-to-close from the sheet's body, on Android. A vertical touch inside a scroll container
// (a ScrollArea viewport, an `overflow-y-auto` pane) is claimed by Chromium/Android WebView as a native
// pan as soon as it passes the touch slop — even when there's nothing to scroll — and the pointer gets
// a `pointercancel`. vaul only follows pointer events, so it stopped after ~20px and, having no
// `pointercancel` handler, never released: the sheet stayed hanging where it was cut off, and only
// the handle could move it (the profile Drawer). So a scroller whose content fits gets
// `touch-action: none`, which leaves the whole gesture to vaul (and sideways swipe-back still gets its
// pointer events); one that really scrolls keeps native scrolling. Re-checked whenever the sheet's
// content or size changes. The `[class*=…]` match is the Tailwind classes we put on panes.
const SCROLLER_SELECTOR = '[data-slot="scroll-area-viewport"], [class*="overflow-y-auto"], [class*="overflow-auto"]'
let lockedScrollers: HTMLElement[] = []
let resizeObserver: ResizeObserver | null = null

function updateScrollerTouchAction() {
  const el = sheetEl()
  if (!el || direction.value !== "bottom") return
  const scrollers = [...el.querySelectorAll<HTMLElement>(SCROLLER_SELECTOR)]
  for (const scroller of lockedScrollers) {
    if (!scrollers.includes(scroller)) scroller.style.touchAction = ""
  }
  lockedScrollers = []
  for (const scroller of scrollers) {
    const fits = scroller.scrollHeight <= scroller.clientHeight + 1
    scroller.style.touchAction = fits ? "none" : ""
    if (fits) lockedScrollers.push(scroller)
    // Its content growing (a list loading in) is what makes it start scrolling.
    resizeObserver?.observe(scroller)
    for (const child of scroller.children) resizeObserver?.observe(child)
  }
}

// Safety net for a scroller that does scroll (pulled down from its top, the browser still takes the
// pan): end vaul's drag the way a `pointerup` would, at the last position the finger was seen, so the
// sheet settles back (or closes, if it was pulled far enough) instead of staying stuck mid-drag.
let lastPointerY = 0
function trackPointer(event: PointerEvent) {
  lastPointerY = event.clientY
}
function onSheetPointerCancel(event: PointerEvent) {
  const el = sheetEl()
  if (!el || direction.value !== "bottom") return
  el.dispatchEvent(new PointerEvent("pointerup", {
    pointerId: event.pointerId,
    pointerType: event.pointerType,
    clientX: event.clientX,
    clientY: lastPointerY,
  }))
}

watch(handleRef, () => {
  const el = sheetEl()
  direction.value = el?.dataset.vaulDrawerDirection
  contentObserver?.disconnect()
  contentObserver = null
  resizeObserver?.disconnect()
  resizeObserver = null
  if (!el || direction.value !== "bottom") return
  // First read happens before any shared height applies (that waits on `direction`).
  measureNaturalHeight()
  resizeObserver = new ResizeObserver(updateScrollerTouchAction)
  resizeObserver.observe(el)
  updateScrollerTouchAction()
  contentObserver = new MutationObserver(() => {
    scheduleMeasure()
    updateScrollerTouchAction()
  })
  // `characterData` too: every lone sheet is now sized from this (see `heightStyle`), so text that
  // changes in place (a count, a loaded name) must re-measure as well.
  contentObserver.observe(el, { childList: true, subtree: true, characterData: true })
}, { immediate: true, flush: "post" })

// A card covered by another Drawer stays hit-testable as far as CSS goes. reka marks every
// dismissable layer under the top one `pointer-events: none`, and that property is inherited: the
// browser restyles the whole subtree each time it flips. Under the chat Drawer that's the entire
// message list, 60–80ms at 4x CPU throttling for ~1800 elements. It landed in the tap that opened a
// sheet over the chat (the profile card, «Прикрепить»), before the sheet's first frame, and again
// once it closed. The card doesn't need it: the covering Drawer's backdrop sits above it
// (`stackLayer`) and catches every tap. So while covered, `auto !important` keeps the computed value
// from ever changing. After the cover leaves the stack, it's held until reka lifts its own `none`
// (when the covering layer unmounts, after its close animation): letting go any earlier would just
// move the restyle to the start of that animation.
const holdPointerEvents = ref(false)
let pointerEventsObserver: MutationObserver | null = null
function stopPointerEventsObserver() {
  pointerEventsObserver?.disconnect()
  pointerEventsObserver = null
}
watch(stackDepth, (depth) => {
  stopPointerEventsObserver()
  if (depth > 0) {
    holdPointerEvents.value = true
    return
  }
  const el = sheetEl()
  if (!holdPointerEvents.value || !el || el.style.pointerEvents !== "none") {
    holdPointerEvents.value = false
    return
  }
  pointerEventsObserver = new MutationObserver(() => {
    if (el.style.pointerEvents === "none") return
    stopPointerEventsObserver()
    holdPointerEvents.value = false
  })
  pointerEventsObserver.observe(el, { attributes: true, attributeFilter: ["style"] })
})

onBeforeUnmount(() => {
  contentObserver?.disconnect()
  resizeObserver?.disconnect()
  stopPointerEventsObserver()
  if (measureFrame !== null) cancelAnimationFrame(measureFrame)
})

// While two or more Drawers are open, every bottom sheet in the stack takes the same height — the
// tallest one's natural height, capped so a band stays free at the top for the receded cards' edges —
// so the cards line up as one pile and each still fits its content (a short one just gets some room
// below, a long one scrolls inside its own ScrollArea). A Drawer on its own keeps its natural height.
// Only updated while this Drawer is open: once it starts closing it leaves the stack, and letting its
// height snap mid-slide would show as a jump — so a closing sheet keeps what it had.
const sharedHeightPx = ref(0)
// This sheet's own natural height (see `measureNaturalHeight`), used when it's the only Drawer open.
const ownNaturalHeightPx = ref(0)
watch([stackSheetHeight, drawerStackSize, isDrawerOpen], ([height, size, open]) => {
  if (open) sharedHeightPx.value = size >= 2 ? height : 0
}, { immediate: true })

// Tallest a bottom sheet gets: the real viewport (`dvh`) minus the status bar and a band above for
// the receded cards' edges. `0px` fallback: an unsupported `env()` would invalidate the whole value.
const SHEET_MAX_HEIGHT = "calc(100dvh - env(safe-area-inset-top, 0px) - 72px)"

// A sheet on its own gets an explicit height too — its own natural height — not just `h-auto` under a
// max-height: with no definite height on the sheet, the `h-full` chain inside (slide strips, panes)
// never resolves, so a ScrollArea there grows to its whole content and gets clipped instead of
// scrolling (group settings' "Роли", the bot's "Основное"). Also means switching between a short list
// and a long section animates the height like the stacked case does.
const heightStyle = computed(() => {
  if (direction.value !== "bottom") return null
  const px = sharedHeightPx.value > 0 ? sharedHeightPx.value : ownNaturalHeightPx.value
  if (px <= 0) return null
  return `min(${px}px, ${SHEET_MAX_HEIGHT})`
})

// Paint order follows the stack, not the DOM. Every Drawer (and its backdrop) is `z-50`, so with two
// open the one later in the document won — and a Drawer's portal can sit earlier than one opened
// before it: «Добавить в друзья», opened from the home screen under a group's Drawer, slid up
// *underneath* that Drawer. While stacked, each layer gets its own step above the one below it
// (backdrop just under its card); a lone Drawer keeps the plain `z-50` (and any class override).
const stackLayer = computed(() => {
  const size = drawerStackSize.value
  if (size < 2 || !isDrawerOpen.value) return null
  const index = size - 1 - stackDepth.value // 0 = bottom of the stack
  return { content: 51 + index * 2, overlay: 50 + index * 2 }
})

const stackStyle = computed(() => {
  const depth = stackDepth.value
  const scale = depth > 0 ? Math.max(0.8, 1 - (STACK_STEP_PX * depth) / window.innerWidth) : 1
  return {
    scale: String(scale),
    translate: `0 ${-STACK_STEP_PX * depth}px`,
    // Bottom sheets shrink toward their top edge, so the `translate` above is what lifts that edge
    // clear of the card in front. Around the center (the default) the shrink would pull the top edge
    // down by about as much as the translate lifts it, and the pile wouldn't show at all.
    transformOrigin: direction.value === "bottom" ? "top center" : undefined,
    // Receded cards dim per step back (the backdrop no longer covers them — see `backdropClass`), so
    // they read as further away. Bottom sheets only lightly, so their peeking edges stay bright
    // against the dimmed page; full-screen cards (the side chat Drawer) have no edge to keep visible
    // and fill the whole background, so they dim harder, standing in for the backdrop.
    filter: depth > 0
      ? `brightness(${Math.max(0.55, 1 - (direction.value === "bottom" ? 0.15 : 0.3) * depth)})`
      : undefined,
    transition: `scale 0.5s ${STACK_EASING}, translate 0.5s ${STACK_EASING}, border-radius 0.5s ${STACK_EASING}, filter 0.5s ${STACK_EASING}`,
    ...(stackLayer.value ? { zIndex: String(stackLayer.value.content) } : {}),
    // No `overflow-y: auto` on the sheet itself, deliberately: vaul hangs a `::after` 200% of the
    // sheet's height below every bottom sheet (to cover the gap when it's dragged up), and a
    // scrollable sheet counts that into its scroll height — the whole sheet, header included,
    // scrolled ~2x its own height into empty space (e.g. "Уведомления" in settings). Long content
    // scrolls inside its own ScrollArea, as sheets already did under their old `max-h-[80vh]`.
    // Before the first measurement, capped to the real viewport (the class's `max-h-[80vh]` is
    // measured against WKWebView's largest viewport, not the current one).
    ...(heightStyle.value
      ? { height: heightStyle.value, maxHeight: "none" }
      : direction.value === "bottom" ? { maxHeight: SHEET_MAX_HEIGHT } : {}),
  }
})

// One backdrop per stack, not per Drawer. Every Drawer brings its own `bg-black/80` backdrop, and
// stacked they compounded until the band above the cards went nearly black. Only the bottom Drawer
// of the stack keeps its backdrop: it sits under every card, so it dims the page alone. The ones
// above stay in place (still catching taps outside their card, which closes it) but transparent —
// had the top one done the dimming instead, it would have darkened the receded cards to the exact
// shade of the dimmed page and their edges would vanish into it. The receded cards get their own
// slight dim below (`brightness`), so they still read as set back.
// A Drawer opened on top of another is decided before its backdrop is first rendered (the stack
// registers it as its `open` flips, ahead of this render), so that backdrop is transparent from its
// very first frame. That's deliberately a background *color*, not `opacity`: the backdrop's own
// enter animation (vaul's / tw-animate's fade-in keyframes) drives `opacity` and outranks any
// inline value while it runs — hiding it via opacity let it visibly fade in first and only vanish
// once the keyframes ended. A transparent color leaves the fade-in with nothing to show.
// If the backdrop Drawer closes while one above stays open, the backdrop role moves to that one,
// cross-fading through `transition-colors` rather than snapping.
// Only updated while open, so a closing Drawer's backdrop doesn't change color mid-fade-out.
const isStackBackdrop = ref(stackDepth.value === drawerStackSize.value - 1 || drawerStackSize.value === 0)
watch([stackDepth, drawerStackSize, isDrawerOpen], ([depth, size, open]) => {
  if (open) isStackBackdrop.value = depth === size - 1
}, { immediate: true })

const backdropClass = computed(() => cn(
  "transition-colors duration-500",
  props.overlayClass,
  !isStackBackdrop.value && "bg-transparent",
))

// Height can't transition to/from `auto`, so every change is animated explicitly: measure before,
// let the style change land, measure after, and play between the two on the same curve as the recede.
watch(heightStyle, async () => {
  const el = sheetEl()
  if (!el) return
  const from = el.offsetHeight
  await nextTick()
  const to = el.offsetHeight
  if (Math.abs(to - from) < 1) return
  heightAnimation = el.animate([{ height: `${from}px` }, { height: `${to}px` }], { duration: 500, easing: STACK_EASING })
}, { flush: "pre" })
</script>

<template>
  <DrawerPortal>
    <DrawerOverlay :class="backdropClass" :style="stackLayer ? { zIndex: stackLayer.overlay } : undefined" />
    <DrawerContent
      data-slot="drawer-content"
      v-bind="{ ...$attrs, ...forwarded }"
      :style="stackStyle"
      :data-stack-depth="stackDepth"
      @pointerdown.capture="trackPointer"
      @pointermove.capture="trackPointer"
      @pointercancel="onSheetPointerCancel"
      :class="cn(
        'group/drawer-content bg-background fixed z-50 flex h-auto flex-col',
        'data-[vaul-drawer-direction=top]:inset-x-0 data-[vaul-drawer-direction=top]:top-0 data-[vaul-drawer-direction=top]:mb-24 data-[vaul-drawer-direction=top]:max-h-[80vh] data-[vaul-drawer-direction=top]:rounded-b-lg',
        'data-[vaul-drawer-direction=bottom]:inset-x-0 data-[vaul-drawer-direction=bottom]:bottom-0 data-[vaul-drawer-direction=bottom]:mt-24 data-[vaul-drawer-direction=bottom]:max-h-[80vh] data-[vaul-drawer-direction=bottom]:rounded-t-lg',
        'data-[vaul-drawer-direction=right]:inset-y-0 data-[vaul-drawer-direction=right]:right-0 data-[vaul-drawer-direction=right]:w-3/4 data-[vaul-drawer-direction=right]:sm:max-w-sm',
        'data-[vaul-drawer-direction=left]:inset-y-0 data-[vaul-drawer-direction=left]:left-0 data-[vaul-drawer-direction=left]:w-3/4 data-[vaul-drawer-direction=left]:sm:max-w-sm',
        props.class,
        // A receded card reads as a separate sheet with all four corners rounded, even the
        // edge-to-edge ones (e.g. the full-width chat Drawer).
        stackDepth > 0 && 'overflow-hidden rounded-2xl',
        holdPointerEvents && 'pointer-events-auto!',
      )"
    >
      <div ref="handleRef" class="bg-muted mx-auto mt-4 hidden h-2 w-[100px] shrink-0 rounded-full group-data-[vaul-drawer-direction=bottom]/drawer-content:block" />
      <slot />
    </DrawerContent>
  </DrawerPortal>
</template>
